import React, { useMemo } from 'react';
import { useIntl } from '@olxpwa-lib/i18n/client';
import { BookingCalendarFormValue } from '~components/AdServiceBooking/types';
import { Button, ButtonLoading } from '@olxui/core/dist/core/Button';
import { TestId } from '~testId';
import { StyledLink } from '~components/shared';
import styled from '@olxui/core/dist/theme/styled';
import { useAppContext } from '~state/App.context';
import { BOOKING_SUMMARY_LOCAL_STORAGE } from '~components/BookingSummary/constants';
import { bookingSummaryKeys } from '~tracking';
import { useFormContext } from 'react-hook-form';
import { AdData } from '@olxpwa-lib/utils-ad/types';

export type BookingSubmitButtonTypes = {
    showAvailability?: boolean;
    ad: AdData;
    isShortTermRentals: boolean;
    renderBookingSubmitMobile?: (element: JSX.Element) => void;
};

const BookingSubmitButton = ({
    showAvailability = false,
    ad,
    isShortTermRentals,
    renderBookingSubmitMobile
}: BookingSubmitButtonTypes) => {
    const {
        watch,
        formState: { isSubmitting }
    } = useFormContext<BookingCalendarFormValue>() ?? {};
    const [dateFrom, dateTo, note] = watch(['dateFrom', 'dateTo', 'note']);
    const { appConfig, tracker } = useAppContext();
    const { formatMessage } = useIntl();
    const { trackEvent } = tracker;
    const { routingAliases } = appConfig;

    const calendarTranslations = useMemo(
        () => ({
            bookingTitle: {
                id: 'service.booking.ad.title',
                defaultMessage: showAvailability
                    ? 'Request for reservation'
                    : 'Check availability'
            },
            bookingDescription: {
                id: 'service.booking.ad.description',
                defaultMessage: showAvailability
                    ? 'Select the date range that you want to request the rental. It is free and non-binding.'
                    : 'It costs nothing and is without obligation.'
            },
            checkoutButton: {
                id: showAvailability
                    ? 'service.booking.ad.request.button.reservation'
                    : 'service.booking.ad.request.button.availiblity',
                defaultMessage: showAvailability
                    ? 'Request a reservation'
                    : 'Send query about availability'
            }
        }),
        [showAvailability]
    );

    const submitButton = () =>
        renderBookingSubmitMobile?.(
            <form>
                <ButtonLoading
                    data-testid={TestId.bookingCalendarSubmitButtonMobile}
                    loading={isSubmitting}
                    type="submit"
                >
                    {formatMessage({
                        ...calendarTranslations.checkoutButton
                    })}
                </ButtonLoading>
            </form>
        );

    const renderSubmitButton = () => dateFrom && submitButton();

    const renderBookingSummaryButton = () =>
        renderBookingSubmitMobile?.(
            <StyledSendRequestButton
                onClick={() => {
                    trackEvent(
                        bookingSummaryKeys.trackEvents.bookingCalendarStart
                    );
                    localStorage.setItem(
                        BOOKING_SUMMARY_LOCAL_STORAGE,
                        JSON.stringify({
                            ad,
                            values: { dateFrom, dateTo, note },
                            // 15 min timestamp
                            timestamp: new Date().getTime() + 900000
                        })
                    );
                }}
                data-testid={TestId.bookingCalendarSummaryButtonMobile}
            >
                <StyledLink
                    to={{
                        pathname: `/${routingAliases.myolx}/booking-summary/${ad.id}`
                    }}
                >
                    {formatMessage({
                        id: 'service.booking.summary.proceed.button',
                        defaultMessage: 'Proceed with request'
                    })}
                </StyledLink>
            </StyledSendRequestButton>
        );

    return dateFrom ? (
        <>
            {isShortTermRentals && showAvailability
                ? renderBookingSummaryButton()
                : renderSubmitButton()}
        </>
    ) : null;
};
export default BookingSubmitButton;

export const StyledSendRequestButton = styled(Button)`
    flex: 1;
`;
